import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  InstrumentGroupDto,
  InstrumentGroupEditDto
} from 'src/store/instrumentgroups/instrumentgroup';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum InstrumentGroupTypes {
  FetchAll = 'instrumentGroups/FetchAll',
  Add = 'instrumentGroups/Add',
  Update = 'instrumentGroups/Update',
  Delete = 'instrumentGroups/Delete',
  Failure = 'instrumentGroups/Failure'
}

export type InstrumentGroupAction =
  | {
      type: InstrumentGroupTypes.FetchAll;
      instrumentGroups: InstrumentGroupDto[];
    }
  | { type: InstrumentGroupTypes.Add; instrumentGroup: InstrumentGroupDto }
  | { type: InstrumentGroupTypes.Update; instrumentGroup: InstrumentGroupDto }
  | { type: InstrumentGroupTypes.Delete; instrumentGroup: InstrumentGroupDto }
  | { type: InstrumentGroupTypes.Failure; errors: any };

export const actionCreators = {
  fetchInstrumentGroups: (
    instrumentGroups: InstrumentGroupDto[]
  ): InstrumentGroupAction => ({
    type: InstrumentGroupTypes.FetchAll,
    instrumentGroups
  }),
  addInstrumentGroup: (
    instrumentGroup: InstrumentGroupDto
  ): InstrumentGroupAction => ({
    type: InstrumentGroupTypes.Add,
    instrumentGroup
  }),
  updateInstrumentGroup: (
    instrumentGroup: InstrumentGroupDto
  ): InstrumentGroupAction => ({
    type: InstrumentGroupTypes.Update,
    instrumentGroup
  }),
  deleteInstrumentGroup: (
    instrumentGroup: InstrumentGroupDto
  ): InstrumentGroupAction => ({
    type: InstrumentGroupTypes.Delete,
    instrumentGroup
  }),
  instrumentGroupRequestFailure: (errors: any): InstrumentGroupAction => ({
    type: InstrumentGroupTypes.Failure,
    errors
  })
};

export const fetchInstrumentGroups = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(InstrumentGroupTypes.FetchAll));
    const { ok, data, error } = await getRequest<InstrumentGroupDto[]>(
      '/instrumentgroups'
    );
    dispatch(requestEnd(InstrumentGroupTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchInstrumentGroups(data))
      : dispatch(actionCreators.instrumentGroupRequestFailure(error));
  };
};

export const createInstrumentGroup = (
  instrumentGroup: InstrumentGroupEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/instrumentgroups',
      instrumentGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('instrumentGroup.created')));
      return dispatch(
        actionCreators.addInstrumentGroup({ ...instrumentGroup, id: data })
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.instrumentGroupRequestFailure(error));
  };
};

export const editInstrumentGroup = (
  id: number,
  instrumentGroup: InstrumentGroupEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(
      `/instrumentgroups/${id}`,
      instrumentGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('instrumentGroup.updated')));
      return dispatch(
        actionCreators.updateInstrumentGroup(
          instrumentGroup as InstrumentGroupDto
        )
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.instrumentGroupRequestFailure(error));
  };
};

export const deleteInstrumentGroup = (
  id: number,
  instrumentGroup: InstrumentGroupDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/instrumentgroups/${id}`,
      instrumentGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('instrumentGroup.deleted')));
      return dispatch(actionCreators.deleteInstrumentGroup(instrumentGroup));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.instrumentGroupRequestFailure(error));
  };
};
